import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { SessionApiService } from '../services/auth/session-api.service';
import { Message } from 'primeng/api/message';
import { Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import * as QRCode from 'qrcode';
import {
  isMfaChallenge,
  MfaSetup,
  SessionErrorCode,
  SessionTokens,
} from '../utils/models/session.model';

/**
 * credentials: username e password.
 * enroll: prima configurazione dell'app autenticatore (QR + primo codice).
 * code: codice a 6 cifre dell'app autenticatore.
 */
type LoginStep = 'credentials' | 'enroll' | 'code';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {
  constructor(
    private auth: AuthService,
    private router: Router,
    private sessionApi: SessionApiService,
    private title: Title
  ) {}
  environment = environment;

  @ViewChild('codeInput') codeInput?: ElementRef<HTMLInputElement>;

  msgs: Message[] = [];
  step: LoginStep = 'credentials';
  loading = false;

  frm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
  });

  mfaFrm = new FormGroup({
    code: new FormControl('', [Validators.required, Validators.pattern(/^\s*\d{3}\s*\d{3}\s*$/)]),
    remember: new FormControl(false),
  });

  /** Token a breve durata ricevuto dopo la password: collega i passi della MFA al login. */
  private mfaToken = '';
  setup?: MfaSetup;
  qrDataUrl?: string;
  secretCopied = false;

  ngOnInit() {
    this.title.setTitle(`${environment.softwareName}`);
    if (this.auth.isLogged()) {
      this.router.navigateByUrl('/main/home');
    }
  }

  /** Il segreto in gruppi di 4 caratteri, più leggibile da ricopiare a mano. */
  get formattedSecret() {
    return this.setup?.secret.match(/.{1,4}/g)?.join(' ') ?? '';
  }

  /**
   * Lo username senza spazi iniziali/finali: spazi e invii accidentali (tastiere, copia-incolla,
   * password manager) farebbero fallire il login senza che si veda il perché. La password invece
   * non si tocca mai.
   */
  private get username() {
    return (this.frm.get('username')!.value ?? '').trim();
  }

  login() {
    if (this.frm.invalid || this.loading || !this.username) return;
    const username = this.username;
    this.loading = true;
    this.msgs = [];

    this.sessionApi
      .login({
        username,
        password: this.frm.get('password')!.value!,
        // se questo dispositivo è stato ricordato per l'utente, il backend salta il codice
        device_token: this.auth.getDeviceToken(username),
      })
      .subscribe({
        next: result => {
          if (!isMfaChallenge(result)) {
            this.complete(result);
            return;
          }
          this.mfaToken = result.mfa_token;
          if (result.enrollment_required) {
            this.startEnrollment();
          } else {
            this.loading = false;
            this.goToStep('code');
          }
        },
        error: (error: HttpErrorResponse) => {
          this.loading = false;
          this.showError(this.errorMessage(error, 'Autenticazione fallita'));
        },
      });
  }

  private startEnrollment() {
    this.sessionApi.setupMfa(this.mfaToken).subscribe({
      next: async setup => {
        this.setup = setup;
        this.qrDataUrl = await QRCode.toDataURL(setup.otpauth_uri, { width: 220, margin: 1 });
        this.loading = false;
        this.goToStep('enroll');
      },
      error: (error: HttpErrorResponse) => this.handleMfaError(error),
    });
  }

  verifyCode() {
    if (this.mfaFrm.invalid || this.loading) return;
    this.loading = true;
    this.msgs = [];

    this.sessionApi
      .verifyMfa({
        mfa_token: this.mfaToken,
        code: this.mfaFrm.get('code')!.value!.replace(/\s+/g, ''),
        remember_device: !!this.mfaFrm.get('remember')!.value,
      })
      .subscribe({
        next: tokens => this.complete(tokens),
        error: (error: HttpErrorResponse) => this.handleMfaError(error),
      });
  }

  private complete(tokens: SessionTokens) {
    this.auth.storeSession(tokens);
    if (tokens.device_token) {
      this.auth.setDeviceToken(this.username, tokens.device_token);
    }
    this.router.navigateByUrl('/main/home');
  }

  private handleMfaError(error: HttpErrorResponse) {
    this.loading = false;
    // Il token della MFA dura pochi minuti: se è scaduto bisogna rifare la password
    if (error.status === 401 || error.error?.code === SessionErrorCode.MFA_TOKEN_EXPIRED) {
      this.backToCredentials('La sessione di accesso è scaduta, effettua di nuovo il login.');
      return;
    }
    this.mfaFrm.get('code')!.reset('');
    this.showError(this.errorMessage(error, 'Verifica non riuscita, riprova.'));
    this.focusCode();
  }

  backToCredentials(message?: string) {
    this.step = 'credentials';
    this.mfaToken = '';
    this.setup = undefined;
    this.qrDataUrl = undefined;
    this.mfaFrm.reset({ code: '', remember: false });
    this.frm.get('password')!.reset('');
    this.msgs = message ? [{ severity: 'warn', summary: 'Attenzione', detail: message }] : [];
  }

  async copySecret() {
    if (!this.setup) return;
    try {
      await navigator.clipboard.writeText(this.setup.secret);
      this.secretCopied = true;
      setTimeout(() => (this.secretCopied = false), 2000);
    } catch {
      // clipboard non disponibile (es. contesto non sicuro): l'utente può ricopiare a mano
    }
  }

  private goToStep(step: LoginStep) {
    this.step = step;
    this.msgs = [];
    this.focusCode();
  }

  private focusCode() {
    setTimeout(() => this.codeInput?.nativeElement.focus());
  }

  private showError(detail: string) {
    this.msgs = [{ severity: 'error', summary: 'Attenzione', detail }];
  }

  private errorMessage(error: HttpErrorResponse, fallback: string) {
    if (error.status === 0) {
      return 'Impossibile contattare il server. Controlla la connessione.';
    }
    const message = error.error?.message;
    return typeof message === 'string' && message ? message : fallback;
  }
}
